import React, { useState } from "react";
import { Modal, Form, Input, Button, notification, Space, Flex } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "styled-components";
import CustomFilterSelect from "../../Component/CustomFilterSelect";
import { fetchUserForSearch, getAllSavedItinerary } from "../../Services/user_question_history";
import { createCampaign } from "../../Services/campaign";

// Caps how tall the Users picker can grow once many tags are selected -
// without this, picking a lot of users pushes the tag box (and the whole
// modal) taller and taller instead of scrolling internally.
const ScrollableUserSelect = styled.div`
  .ant-select-selector {
    max-height: 150px;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .ant-select-selection-overflow {
    flex-wrap: wrap;
  }
`;

let rowKeyCounter = 0;
const newRow = () => ({ key: rowKeyCounter++, userIds: [], itineraryId: null });

// Global search across every user's itineraries by headline/from_location/
// to_location/id - not scoped to whichever users are selected in the row,
// and not a user-identity search (the Users picker below already covers
// that). userName kept separate from the label so renderOption can show it
// on its own line below the headline instead of inline.
const fetchItinerariesGlobal = async (page, search) => {
  const result = await getAllSavedItinerary({ skip: page * 10, limit: 10, search });
  return (result?.data || []).map((it) => ({
    label: it.itineary_headline_text,
    value: it.id,
    userName: it.user_name,
  }));
};

const renderItineraryOption = (option) => (
  <div style={{ lineHeight: 1.3, padding: "2px 0" }}>
    <div>{option.label}</div>
    <div style={{ fontSize: 12, color: "#888" }}>
      ID: {option.value}
      {option.userName ? ` · ${option.userName}` : ""}
    </div>
  </div>
);

const CreateCampaignModal = ({ isModalVisible, handleOk, handleCancel }) => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([newRow()]);
  const [isSending, setIsSending] = useState(false);

  const resetForm = () => {
    form.resetFields();
    setRows([newRow()]);
  };

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (key) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const invalidRow = rows.find((r) => r.userIds.length === 0 || !r.itineraryId);
      if (invalidRow) {
        return notification.error({ message: "Every row needs at least one user and an itinerary selected" });
      }

      const recipients = rows.flatMap((r) =>
        r.userIds.filter((uid) => Number.isInteger(uid)).map((uid) => ({ user_id: uid, user_itinerary_id: r.itineraryId }))
      );
      if (recipients.length === 0) {
        return notification.error({ message: "Please select at least one recipient" });
      }

      setIsSending(true);
      // template_id intentionally omitted - falls back to the default
      // configured in the DB config table (ITINERARY_PROMO_EMAIL_TEMPLATE_ID).
      await createCampaign({ title: values.title, recipients });
      resetForm();
      notification.success({ message: "Campaign created and emails are being sent" });
      handleOk();
    } catch (error) {
      if (!error?.errorFields) {
        notification.error({ message: "Failed to create campaign" });
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal
      title="Create Email Campaign"
      open={isModalVisible}
      footer={null}
      width={720}
      onCancel={() => {
        resetForm();
        handleCancel();
      }}
      maskClosable={false}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Campaign Title"
          name="title"
          rules={[{ required: true, message: "Title is required" }]}
        >
          <Input placeholder="Enter campaign title" maxLength={255} />
        </Form.Item>

        <Flex justify="space-between" align="center" style={{ marginBottom: 18 }}>
          <div style={{ fontWeight: 600 }}>
            Recipients &mdash; each row emails its selected users the same itinerary
          </div>
          <Button
            icon={<PlusOutlined />}
            onClick={addRow}
            // style={{ color: '#faad14', borderColor: "#faad14", backgroundColor: "#fff"}}
            // variant="outlined"
            color="primary" variant="outlined"
          >
            Add Row
          </Button>
        </Flex>

        {rows.map((row) => (
          <Space key={row.key} align="start" style={{ display: "flex", marginBottom: 12, width: "100%" }}>
            <ScrollableUserSelect style={{ width: 280 }}>
              <CustomFilterSelect
                fetchOptions={fetchUserForSearch}
                label="Users"
                filterKey={`users_${row.key}`}
                mode="multiple"
                value={row.userIds}
                onChange={(_key, val) => updateRow(row.key, { userIds: val })}
                renderOption={(option) => `#${option.value} ${option.label} (${option.email})`}
                placeholder="Search by name, email, id, or paste emails"
                copyPaste={true}
              />
            </ScrollableUserSelect>
            <div style={{ width: 300 }}>
              <CustomFilterSelect
                fetchOptions={fetchItinerariesGlobal}
                label="Itinerary"
                filterKey={`itinerary_${row.key}`}
                value={row.itineraryId}
                onChange={(_key, val) => updateRow(row.key, { itineraryId: val })}
                renderOption={renderItineraryOption}
                placeholder="Search by headline, location, or id"
              />
            </div>
            <Button
              icon={<DeleteOutlined />}
              onClick={() => removeRow(row.key)}
              disabled={rows.length === 1}
            />
          </Space>
        ))}

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" onClick={handleSubmit} loading={isSending}>
              Create Campaign
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateCampaignModal;
